import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    TierManagementPlanResponse,
    getApiErrorMessage,
    getApiResponseMessage,
    getTierManagementPlans,
    updateTierManagementPlan,
} from '../../services/subscriptionPaymentService';
import { RootState } from '../store';

const MONEY_INPUT_REGEX = /^(?:\d+(?:[.,]\d{1,2})?|[.,]\d{1,2})$/;
const TRIAL_DURATION_ONE_WEEK = '1_week';
const TRIAL_DURATION_TWO_WEEKS = '2_weeks';
const TRIAL_DURATION_ONE_MONTH = '1_month';

type TierPlan = TierManagementPlanResponse;

const parseUnitAmount = (value: string): number | null => {
    const normalized = value.trim().replace(/\s+/g, '');
    if (!normalized || !MONEY_INPUT_REGEX.test(normalized)) return null;

    const parsed = Number(normalized.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    return parsed;
};

const parseModelsAllowed = (value: string): number | null => {
    const normalized = value.trim();
    if (!normalized) return null;
    if (!/^\d+$/.test(normalized)) return null;

    const parsed = Number(normalized);
    if (!Number.isInteger(parsed) || parsed < 0) return null;

    return parsed;
};

const normalizeTrialDuration = (value?: string | null): string => {
    if (value === TRIAL_DURATION_ONE_WEEK || value === TRIAL_DURATION_TWO_WEEKS || value === TRIAL_DURATION_ONE_MONTH) {
        return value;
    }

    return TRIAL_DURATION_ONE_MONTH;
};

interface TierManagementState {
    plans: TierPlan[];
    loading: boolean;
    saving: boolean;
    editPlan: TierPlan | null;
    name: string;
    description: string;
    unitAmount: string;
    modelsAllowedEnabled: boolean;
    modelsAllowed: string;
    trialSupported: boolean;
    trialDuration: string;
}

const initialState: TierManagementState = {
    plans: [],
    loading: false,
    saving: false,
    editPlan: null,
    name: '',
    description: '',
    unitAmount: '',
    modelsAllowedEnabled: false,
    modelsAllowed: '',
    trialSupported: false,
    trialDuration: TRIAL_DURATION_ONE_MONTH,
};

export const fetchTierPlans = createAsyncThunk<TierPlan[], void, { rejectValue: string }>(
    'tierManagement/fetchTierPlans',
    async (_, thunkAPI) => {
        try {
            const response = await getTierManagementPlans();
            if (!response.success) {
                return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
            }

            return response.result ?? [];
        } catch (error) {
            return thunkAPI.rejectWithValue(getApiErrorMessage(error));
        }
    }
);

export const saveTierPlanChanges = createAsyncThunk<
    { updated: boolean; updatedSubscriptionsCount: number },
    void,
    { state: RootState; rejectValue: string }
>('tierManagement/saveTierPlanChanges', async (_, thunkAPI) => {
    const state = thunkAPI.getState().tiermanagement;
    const editPlan = state.editPlan;

    if (!editPlan) {
        return thunkAPI.rejectWithValue('BE_SUBSCRIPTION_PAYMENT_OBJECT_INVALID');
    }

    const nextName = state.name.trim();
    const parsedAmount = parseUnitAmount(state.unitAmount);
    const nextDescriptionRaw = state.description.trim();
    const nextDescription = nextDescriptionRaw.length > 0 ? nextDescriptionRaw : null;
    const parsedModelsAllowed = state.modelsAllowedEnabled ? parseModelsAllowed(state.modelsAllowed) : null;

    if (!nextName || parsedAmount == null || (state.modelsAllowedEnabled && parsedModelsAllowed == null)) {
        return thunkAPI.rejectWithValue('BE_SUBSCRIPTION_PAYMENT_OBJECT_INVALID');
    }

    const existingName = (editPlan.name ?? '').trim();
    const existingDescriptionRaw = (editPlan.description ?? '').trim();
    const existingDescription = existingDescriptionRaw.length > 0 ? existingDescriptionRaw : null;
    const existingAmount = editPlan.unitAmount;
    const hasSameAmount = typeof existingAmount === 'number'
        ? Math.abs(existingAmount - parsedAmount) < 0.000001
        : false;
    const existingModelsAllowed = editPlan.modelsAllowed ?? null;
    const existingTrialSupported = editPlan.trialSupported === true;
    const existingTrialDuration = normalizeTrialDuration(editPlan.trialDuration);
    const selectedTrialDuration = normalizeTrialDuration(state.trialDuration);
    const isTrialDurationChanged = state.trialSupported && selectedTrialDuration !== existingTrialDuration;

    const isUnchanged =
        nextName === existingName &&
        nextDescription === existingDescription &&
        hasSameAmount &&
        parsedModelsAllowed === existingModelsAllowed &&
        state.trialSupported === existingTrialSupported &&
        !isTrialDurationChanged;

    if (isUnchanged) {
        return { updated: false, updatedSubscriptionsCount: 0 };
    }

    try {
        const response = await updateTierManagementPlan({
            stripePriceId: editPlan.stripePriceId,
            name: nextName,
            modelsAllowed: parsedModelsAllowed,
            trialSupported: state.trialSupported,
            trialDuration: selectedTrialDuration,
            description: nextDescription,
            unitAmount: parsedAmount,
        });

        if (!response.success) {
            return thunkAPI.rejectWithValue(getApiResponseMessage(response.message));
        }

        const updatedSubscriptionsCount = response.result?.updatedSubscriptionsCount ?? 0;
        await thunkAPI.dispatch(fetchTierPlans());

        return { updated: true, updatedSubscriptionsCount };
    } catch (error) {
        return thunkAPI.rejectWithValue(getApiErrorMessage(error));
    }
});

export const tierManagementSlice = createSlice({
    name: 'tiermanagement',
    initialState,
    reducers: {
        openTierPlanEditor: (state, action: PayloadAction<TierPlan>) => {
            const plan = action.payload;
            state.editPlan = plan;
            state.name = plan.name || '';
            state.description = plan.description || '';
            state.unitAmount = plan.unitAmount != null ? plan.unitAmount.toString() : '';
            state.modelsAllowedEnabled = plan.modelsAllowed != null;
            state.modelsAllowed = plan.modelsAllowed != null ? plan.modelsAllowed.toString() : '';
            state.trialSupported = plan.trialSupported === true;
            state.trialDuration = normalizeTrialDuration(plan.trialDuration);
        },
        closeTierPlanEditor: (state) => {
            if (state.saving) {
                return;
            }

            state.editPlan = null;
            state.name = '';
            state.description = '';
            state.unitAmount = '';
            state.modelsAllowedEnabled = false;
            state.modelsAllowed = '';
            state.trialSupported = false;
            state.trialDuration = TRIAL_DURATION_ONE_MONTH;
        },
        setTierName: (state, action: PayloadAction<string>) => {
            state.name = action.payload;
        },
        setTierDescription: (state, action: PayloadAction<string>) => {
            state.description = action.payload;
        },
        setTierUnitAmount: (state, action: PayloadAction<string>) => {
            state.unitAmount = action.payload;
        },
        setTierModelsAllowedEnabled: (state, action: PayloadAction<boolean>) => {
            state.modelsAllowedEnabled = action.payload;
            if (!action.payload) {
                state.modelsAllowed = '';
            }
        },
        setTierModelsAllowed: (state, action: PayloadAction<string>) => {
            state.modelsAllowed = action.payload;
        },
        setTierTrialSupported: (state, action: PayloadAction<boolean>) => {
            state.trialSupported = action.payload;
        },
        setTierTrialDuration: (state, action: PayloadAction<string>) => {
            state.trialDuration = normalizeTrialDuration(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTierPlans.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchTierPlans.fulfilled, (state, action) => {
            state.loading = false;
            state.plans = action.payload;
        });
        builder.addCase(fetchTierPlans.rejected, (state) => {
            state.loading = false;
            state.plans = [];
        });

        builder.addCase(saveTierPlanChanges.pending, (state) => {
            state.saving = true;
        });
        builder.addCase(saveTierPlanChanges.fulfilled, (state, action) => {
            state.saving = false;
            if (!action.payload.updated) {
                state.editPlan = null;
                return;
            }

            state.editPlan = null;
            state.name = '';
            state.description = '';
            state.unitAmount = '';
            state.modelsAllowedEnabled = false;
            state.modelsAllowed = '';
            state.trialSupported = false;
            state.trialDuration = TRIAL_DURATION_ONE_MONTH;
        });
        builder.addCase(saveTierPlanChanges.rejected, (state) => {
            state.saving = false;
        });
    },
});

export const {
  openTierPlanEditor,
  closeTierPlanEditor,
  setTierName,
  setTierDescription,
  setTierUnitAmount,
  setTierModelsAllowedEnabled,
  setTierModelsAllowed,
  setTierTrialSupported,
  setTierTrialDuration,
} = tierManagementSlice.actions;

export const tierManagementSelector = (state: RootState) => state.tiermanagement;
